import { requireSupabase } from "./supabase";
import type { WarehouseCode } from "../types";

export type DeliveryNoteSnapshot = {
  id: string;
  storagePath: string;
  originalFileName: string | null;
  mimeType: string;
  sizeBytes: number | null;
  pageCount: number | null;
  documentSource: "camera" | "network_scanner";
  capturedAt: string;
};

export type ReceiptSnapshot = {
  id: string;
  workOrderId: string;
  orderNumber: string;
  orderDescription: string | null;
  supplier: string;
  department: string;
  warehouseId: string;
  warehouse: WarehouseCode;
  warehouseName: string;
  location: string;
  packageCount: number;
  stockedPackages: number;
  pickedUpPackages: number;
  returnedPackages: number;
  receivedAt: string;
  status: "draft" | "stored" | "partially_picked_up" | "picked_up";
  returnStatus: "registered" | "picked_up" | null;
  returnRegisteredAt: string | null;
  returnPickedUpAt: string | null;
  deliveryNoteNumber: string | null;
  documents: DeliveryNoteSnapshot[];
  documentId: string | null;
  documentStoragePath: string | null;
  documentMimeType: string | null;
  easyTecId: string | null;
  easyTecStatus: "open" | "uploaded" | "not_required" | null;
};

export type PackageSnapshot = {
  id: string;
  code: string;
  packageNumber: number;
  status: "stocked" | "picked_up" | "returned";
  returnedAt: string | null;
  receiptId: string;
  orderNumber: string;
  supplier: string;
  department: string;
  warehouseId: string;
  warehouse: WarehouseCode;
  warehouseName: string;
  location: string;
};

export type PickupSnapshot = {
  id: string;
  warehouse: WarehouseCode;
  warehouseName: string;
  technician: string;
  packageCount: number;
  packageCodes: string[];
  orderNumbers: string[];
  signedAt: string | null;
  createdAt: string;
  signatureStoragePath: string | null;
};

export type EasyTecSnapshot = {
  id: string;
  status: "open" | "uploaded" | "not_required";
  completedAt: string | null;
  receiptId: string;
  orderNumber: string;
  supplier: string;
  receivedAt: string;
  documentStoragePath: string;
  documentMimeType: string;
  warehouse: WarehouseCode;
  warehouseName: string;
};

export type ScannerAgentSnapshot = {
  warehouseId: string;
  agentId: string;
  deviceName: string;
  sourceFolder: string;
  agentVersion: string;
  status: "online" | "folder_unreachable" | "error";
  lastHeartbeatAt: string;
  lastImportAt: string | null;
  lastImportFileName: string | null;
  lastError: string | null;
};

export type WarehouseSnapshot = {
  warehouses: Array<{ id: string; code: WarehouseCode; name: string }>;
  receipts: ReceiptSnapshot[];
  packages: PackageSnapshot[];
  pickups: PickupSnapshot[];
  easyTec: EasyTecSnapshot[];
  scannerAgents: ScannerAgentSnapshot[];
  pendingPrintJobs: number;
  pendingPrintJobsByWarehouse: Record<WarehouseCode, number>;
};

type WarehouseRow = { id: string; code: WarehouseCode; name: string };
type ReceiptRow = { id: string; warehouse_id: string; work_order_id: string; supplier_id: string; storage_location_id: string; received_at: string; delivery_note_number: string | null; package_count: number; status: ReceiptSnapshot["status"] };
type WorkOrderRow = { id: string; order_number: string; department_id: string | null; description: string | null };
type SupplierRow = { id: string; name: string };
type LocationRow = { id: string; code: string };
type DepartmentRow = { id: string; name: string };
type PackageRow = { id: string; package_code: string; package_number: number; goods_receipt_id: string; status: PackageSnapshot["status"]; returned_at: string | null; order_return_id: string | null };
type DocumentRow = {
  id: string;
  goods_receipt_id: string | null;
  storage_path: string;
  original_file_name: string | null;
  mime_type: string;
  size_bytes: number | null;
  page_count: number | null;
  document_source: DeliveryNoteSnapshot["documentSource"];
  captured_at: string;
};
type DocumentReceiptRow = { document_id: string; goods_receipt_id: string };
type EasyTecRow = { id: string; document_id: string; status: EasyTecSnapshot["status"]; completed_at: string | null };
type PickupRow = { id: string; warehouse_id: string; technician_id: string; signature_storage_path: string | null; signed_at: string | null; created_at: string };
type PickupPackageRow = { pickup_id: string; package_id: string };
type TechnicianRow = { id: string; full_name: string };
type PrintJobRow = { id: string; warehouse_id: string; status: string };
type ScannerAgentRow = {
  warehouse_id: string;
  agent_id: string;
  device_name: string;
  source_folder: string;
  agent_version: string;
  status: ScannerAgentSnapshot["status"];
  last_heartbeat_at: string;
  last_import_at: string | null;
  last_import_file_name: string | null;
  last_error: string | null;
};
type OrderReturnRow = {
  id: string;
  work_order_id: string;
  warehouse_id: string;
  status: "registered" | "picked_up";
  registered_at: string;
  picked_up_at: string | null;
};

function mapById<T extends { id: string }>(rows: T[]) {
  return new Map(rows.map((row) => [row.id, row]));
}

export async function loadWarehouseSnapshot(): Promise<WarehouseSnapshot> {
  const client = requireSupabase();
  const [
    warehouseResult,
    receiptResult,
    orderResult,
    supplierResult,
    locationResult,
    departmentResult,
    packageResult,
    documentResult,
    documentReceiptResult,
    easyTecResult,
    pickupResult,
    pickupPackageResult,
    technicianResult,
    printJobResult,
    returnResult,
    scannerAgentResult
  ] = await Promise.all([
    client.from("warehouses").select("id,code,name"),
    client.from("goods_receipts").select("id,warehouse_id,work_order_id,supplier_id,storage_location_id,received_at,delivery_note_number,package_count,status").order("received_at", { ascending: false }),
    client.from("work_orders").select("id,order_number,department_id,description"),
    client.from("suppliers").select("id,name"),
    client.from("storage_locations").select("id,code"),
    client.from("departments").select("id,name"),
    client.from("packages").select("id,package_code,package_number,goods_receipt_id,status,returned_at,order_return_id").order("package_code"),
    client.from("delivery_note_documents").select("id,goods_receipt_id,storage_path,original_file_name,mime_type,size_bytes,page_count,document_source,captured_at"),
    client.from("delivery_note_document_receipts").select("document_id,goods_receipt_id"),
    client.from("easytec_exports").select("id,document_id,status,completed_at").order("created_at", { ascending: false }),
    client.from("pickups").select("id,warehouse_id,technician_id,signature_storage_path,signed_at,created_at").order("created_at", { ascending: false }),
    client.from("pickup_packages").select("pickup_id,package_id"),
    client.from("technicians").select("id,full_name"),
    client.from("label_print_jobs").select("id,warehouse_id,status"),
    client.from("order_returns").select("id,work_order_id,warehouse_id,status,registered_at,picked_up_at").order("registered_at", { ascending: false }),
    client.from("scanner_agent_status").select("warehouse_id,agent_id,device_name,source_folder,agent_version,status,last_heartbeat_at,last_import_at,last_import_file_name,last_error")
  ]);

  const firstError = warehouseResult.error
    || receiptResult.error
    || orderResult.error
    || supplierResult.error
    || locationResult.error
    || departmentResult.error
    || packageResult.error
    || documentResult.error
    || documentReceiptResult.error
    || easyTecResult.error
    || pickupResult.error
    || pickupPackageResult.error
    || technicianResult.error
    || printJobResult.error
    || returnResult.error
    || scannerAgentResult.error;
  if (firstError) throw new Error(firstError.message);

  const warehouses = mapById((warehouseResult.data ?? []) as WarehouseRow[]);
  const workOrders = mapById((orderResult.data ?? []) as WorkOrderRow[]);
  const suppliers = mapById((supplierResult.data ?? []) as SupplierRow[]);
  const locations = mapById((locationResult.data ?? []) as LocationRow[]);
  const departments = mapById((departmentResult.data ?? []) as DepartmentRow[]);
  const packageRows = (packageResult.data ?? []) as PackageRow[];
  const packagesById = mapById(packageRows);
  const documentRows = (documentResult.data ?? []) as DocumentRow[];
  const documentReceiptRows = (documentReceiptResult.data ?? []) as DocumentReceiptRow[];
  const easyTecRows = (easyTecResult.data ?? []) as EasyTecRow[];
  const returnRows = (returnResult.data ?? []) as OrderReturnRow[];

  const receipts = ((receiptResult.data ?? []) as ReceiptRow[]).map<ReceiptSnapshot>((row) => {
    const warehouse = warehouses.get(row.warehouse_id);
    const workOrder = workOrders.get(row.work_order_id);
    const department = workOrder?.department_id ? departments.get(workOrder.department_id) : null;
    const receiptPackages = packageRows.filter((item) => item.goods_receipt_id === row.id);
    const receiptReturns = returnRows.filter((item) => item.work_order_id === row.work_order_id && item.warehouse_id === row.warehouse_id);
    const activeReturn = receiptPackages.some((item) => item.status === "stocked")
      ? receiptReturns.find((item) => item.status === "registered") ?? null
      : null;
    const completedReturn = receiptReturns.find((returnItem) => returnItem.status === "picked_up" && receiptPackages.some((packageItem) => packageItem.order_return_id === returnItem.id)) ?? null;
    const currentReturn = activeReturn ?? completedReturn;
    const linkedDocumentIds = new Set(documentReceiptRows
      .filter((link) => link.goods_receipt_id === row.id)
      .map((link) => link.document_id));
    const documents = documentRows
      .filter((item) => item.goods_receipt_id === row.id || linkedDocumentIds.has(item.id))
      .filter((item, index, items) => items.findIndex((candidate) => candidate.id === item.id) === index)
      .map<DeliveryNoteSnapshot>((item) => ({
        id: item.id,
        storagePath: item.storage_path,
        originalFileName: item.original_file_name,
        mimeType: item.mime_type,
        sizeBytes: item.size_bytes,
        pageCount: item.page_count,
        documentSource: item.document_source,
        capturedAt: item.captured_at
      }));
    const document = documents[0] ?? null;
    const easyTec = document ? easyTecRows.find((item) => item.document_id === document.id) ?? null : null;
    return {
      id: row.id,
      workOrderId: row.work_order_id,
      orderNumber: workOrder?.order_number ?? "–",
      orderDescription: workOrder?.description ?? null,
      supplier: suppliers.get(row.supplier_id)?.name ?? "–",
      department: department?.name ?? "Nicht zugeordnet",
      warehouseId: row.warehouse_id,
      warehouse: warehouse?.code ?? "MZ",
      warehouseName: warehouse?.name ?? "Unbekanntes Lager",
      location: locations.get(row.storage_location_id)?.code ?? "–",
      packageCount: row.package_count,
      stockedPackages: receiptPackages.filter((item) => item.status === "stocked").length,
      pickedUpPackages: receiptPackages.filter((item) => item.status === "picked_up").length,
      returnedPackages: receiptPackages.filter((item) => item.status === "returned").length,
      receivedAt: row.received_at,
      status: row.status,
      returnStatus: currentReturn?.status ?? null,
      returnRegisteredAt: currentReturn?.registered_at ?? null,
      returnPickedUpAt: currentReturn?.picked_up_at ?? null,
      deliveryNoteNumber: row.delivery_note_number,
      documents,
      documentId: document?.id ?? null,
      documentStoragePath: document?.storagePath ?? null,
      documentMimeType: document?.mimeType ?? null,
      easyTecId: easyTec?.id ?? null,
      easyTecStatus: easyTec?.status ?? null
    };
  });
  const receiptsById = mapById(receipts);

  const packages = packageRows.map<PackageSnapshot>((row) => {
    const receipt = receiptsById.get(row.goods_receipt_id);
    return {
      id: row.id,
      code: row.package_code,
      packageNumber: row.package_number,
      status: row.status,
      returnedAt: row.returned_at,
      receiptId: row.goods_receipt_id,
      orderNumber: receipt?.orderNumber ?? "–",
      supplier: receipt?.supplier ?? "–",
      department: receipt?.department ?? "–",
      warehouseId: receipt?.warehouseId ?? "",
      warehouse: receipt?.warehouse ?? "MZ",
      warehouseName: receipt?.warehouseName ?? "Unbekanntes Lager",
      location: receipt?.location ?? "–"
    };
  });

  const technicians = mapById((technicianResult.data ?? []) as TechnicianRow[]);
  const selectedPackages = (pickupPackageResult.data ?? []) as PickupPackageRow[];
  const pickups = ((pickupResult.data ?? []) as PickupRow[]).map<PickupSnapshot>((row) => {
    const packageIds = selectedPackages.filter((item) => item.pickup_id === row.id).map((item) => item.package_id);
    const pickupPackages = packageIds.map((id) => packagesById.get(id)).filter((item): item is PackageRow => Boolean(item));
    const orderNumbers = [...new Set(pickupPackages.map((item) => receiptsById.get(item.goods_receipt_id)?.orderNumber ?? "–"))];
    return {
      id: row.id,
      warehouse: warehouses.get(row.warehouse_id)?.code ?? "MZ",
      warehouseName: warehouses.get(row.warehouse_id)?.name ?? "Unbekanntes Lager",
      technician: technicians.get(row.technician_id)?.full_name ?? "Unbekannter Monteur",
      packageCount: pickupPackages.length,
      packageCodes: pickupPackages.map((item) => item.package_code),
      orderNumbers,
      signedAt: row.signed_at,
      createdAt: row.created_at,
      signatureStoragePath: row.signature_storage_path
    };
  });

  const receiptByDocumentId = new Map<string, ReceiptSnapshot>();
  documentRows.forEach((document) => {
    const linkedReceiptId = document.goods_receipt_id
      ?? documentReceiptRows.find((link) => link.document_id === document.id)?.goods_receipt_id;
    const receipt = linkedReceiptId ? receiptsById.get(linkedReceiptId) : null;
    if (receipt) receiptByDocumentId.set(document.id, receipt);
  });
  const easyTec = easyTecRows.map<EasyTecSnapshot>((row) => {
    const document = documentRows.find((item) => item.id === row.document_id);
    const receipt = receiptByDocumentId.get(row.document_id);
    return {
      id: row.id,
      status: row.status,
      completedAt: row.completed_at,
      receiptId: receipt?.id ?? "",
      orderNumber: receipt?.orderNumber ?? "–",
      supplier: receipt?.supplier ?? "–",
      receivedAt: receipt?.receivedAt ?? "",
      documentStoragePath: document?.storage_path ?? "",
      documentMimeType: document?.mime_type ?? "application/octet-stream",
      warehouse: receipt?.warehouse ?? "MZ",
      warehouseName: receipt?.warehouseName ?? "Unbekanntes Lager"
    };
  });

  const printJobs = (printJobResult.data ?? []) as PrintJobRow[];
  const openPrintJobs = printJobs.filter((row) => row.status === "queued" || row.status === "failed");
  const scannerAgents = ((scannerAgentResult.data ?? []) as ScannerAgentRow[]).map<ScannerAgentSnapshot>((row) => ({
    warehouseId: row.warehouse_id,
    agentId: row.agent_id,
    deviceName: row.device_name,
    sourceFolder: row.source_folder,
    agentVersion: row.agent_version,
    status: row.status,
    lastHeartbeatAt: row.last_heartbeat_at,
    lastImportAt: row.last_import_at,
    lastImportFileName: row.last_import_file_name,
    lastError: row.last_error
  }));
  const pendingPrintJobsByWarehouse = (["OB", "MZ"] as WarehouseCode[]).reduce<Record<WarehouseCode, number>>((result, code) => {
    result[code] = openPrintJobs.filter((row) => warehouses.get(row.warehouse_id)?.code === code).length;
    return result;
  }, { OB: 0, MZ: 0 });

  return {
    warehouses: [...warehouses.values()],
    receipts,
    packages,
    pickups,
    easyTec,
    scannerAgents,
    pendingPrintJobs: openPrintJobs.length,
    pendingPrintJobsByWarehouse
  };
}

export function formatDateTime(value: string | null) {
  if (!value) return "–";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" }).format(date);
}

export function receiptStatusPresentation(receipt: Pick<ReceiptSnapshot, "status" | "returnStatus">): {
  label: string;
  tone: "neutral" | "success" | "warning" | "danger";
} {
  if (receipt.returnStatus === "registered") return { label: "Retoure angemeldet", tone: "warning" };
  if (receipt.returnStatus === "picked_up") return { label: "Retoure abgeholt", tone: "success" };
  if (receipt.status === "draft") return { label: "Entwurf", tone: "warning" };
  if (receipt.status === "partially_picked_up") return { label: "Teilweise abgeholt", tone: "warning" };
  if (receipt.status === "picked_up") return { label: "Abgeholt", tone: "neutral" };
  return { label: "Eingelagert", tone: "success" };
}
